import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Copy, Mail, Phone, Check, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const SharePaymentLinkModal = ({ open, onClose, checkoutUrl, customerEmail, customerPhone, bookingDetails }) => {
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    toast.success('Payment link copied to clipboard!');
    setTimeout(() => setCopied(false), 3000);
  };

  const handleEmailCustomer = async () => {
    try {
      setSendingEmail(true);
      await api.post('/admin/send-payment-link-email', {
        email: customerEmail,
        checkoutUrl: checkoutUrl,
        bookingDetails: bookingDetails
      });
      toast.success(`Payment link sent to ${customerEmail}`);
    } catch (error) {
      console.error('Email error:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSMSCustomer = async () => {
    try {
      setSendingSMS(true);
      await api.post('/admin/send-payment-link-sms', {
        phone: customerPhone,
        checkoutUrl: checkoutUrl,
        bookingDetails: bookingDetails
      });
      toast.success(`Payment link sent to ${customerPhone}`);
    } catch (error) {
      console.error('SMS error:', error);
      toast.error('Failed to send SMS');
    } finally {
      setSendingSMS(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Check className="w-6 h-6 text-green-600" />
            Payment Link Created!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Success Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              ✅ Booking created successfully! Share this payment link with your customer.
            </p>
          </div>

          {/* Payment Link Display */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">
              Payment Link
            </label>
            <div className="flex gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 font-mono">
                {/* Truncate long URL: show first 25 chars + ... + last 15 chars */}
                {checkoutUrl && checkoutUrl.length > 50 
                  ? `${checkoutUrl.substring(0, 25)}...${checkoutUrl.substring(checkoutUrl.length - 15)}`
                  : checkoutUrl
                }
              </div>
              <Button
                onClick={handleCopy}
                variant="outline"
                className="flex-shrink-0"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Send Options */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700">Or send directly:</p>
            
            {/* Email Button */}
            <Button
              onClick={handleEmailCustomer}
              disabled={sendingEmail || !customerEmail}
              style={{ backgroundColor: '#2563EB' }}
              className="w-full hover:opacity-90 justify-start text-white"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Email to {customerEmail || 'customer'}
                </>
              )}
            </Button>

            {/* SMS Button */}
            <Button
              onClick={handleSMSCustomer}
              disabled={sendingSMS || !customerPhone}
              className="w-full bg-green-600 hover:bg-green-700 justify-start"
            >
              {sendingSMS ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4 mr-2" />
                  SMS to {customerPhone || 'customer'}
                </>
              )}
            </Button>
          </div>

          {/* Booking Details Summary */}
          {bookingDetails && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1">
              <p className="font-semibold text-gray-700 mb-2">Booking Details:</p>
              <p><span className="text-gray-600">Service:</span> {bookingDetails.serviceName}</p>
              <p><span className="text-gray-600">Date:</span> {(() => {
                const date = new Date(bookingDetails.preferredDate + 'T00:00:00');
                const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const day = date.getDate();
                const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                               day === 2 || day === 22 ? 'nd' : 
                               day === 3 || day === 23 ? 'rd' : 'th';
                
                // Format time
                const [hours, minutes] = bookingDetails.preferredTime.split(':');
                const hour = parseInt(hours);
                const ampm = hour >= 12 ? 'pm' : 'am';
                const hour12 = hour % 12 || 12;
                const timeStr = `${hour12}:${minutes}${ampm}`;
                
                return `${dateStr}${suffix} at ${timeStr} CST`;
              })()}</p>
              <p><span className="text-gray-600">Customer:</span> {bookingDetails.customerName}</p>
              <p><span className="text-gray-600">Amount:</span> ${bookingDetails.totalAmount}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Done
            </Button>
            <Button
              onClick={() => window.open(checkoutUrl, '_blank')}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePaymentLinkModal;
